import { useCallback, useState } from "react";

/**
 * Owns the "which editing dialog is open, and on what" state for the Output page.
 *
 * These six pieces of state were declared inline in ProjectOutput.jsx and are all
 * the same shape of thing: a nullable target that both opens a dialog and tells it
 * what it's acting on. Grouping them keeps the page component about wiring rather
 * than bookkeeping, and gives one obvious place to close everything.
 *
 * Each target is deliberately independent — the Output page can legitimately have
 * a context menu open over a node while a placement is pending, so this does not
 * enforce mutual exclusion.
 */
export function useEditingDialogs() {
  const [contextMenu, setContextMenu] = useState(null); // {x, y, nodeId}
  const [addChildOf, setAddChildOf] = useState(null); // node to add a child under
  const [moveSource, setMoveSource] = useState(null); // node being moved
  const [renameTarget, setRenameTarget] = useState(null); // node being renamed
  const [removeTarget, setRemoveTarget] = useState(null); // node pending remove confirm
  const [peerLabelTarget, setPeerLabelTarget] = useState(null); // {sourceId, targetId}

  /** Dismiss every dialog at once — used on Escape and after a destructive edit. */
  const closeAllDialogs = useCallback(() => {
    setContextMenu(null);
    setAddChildOf(null);
    setMoveSource(null);
    setRenameTarget(null);
    setRemoveTarget(null);
    setPeerLabelTarget(null);
  }, []);

  return {
    contextMenu,
    setContextMenu,
    addChildOf,
    setAddChildOf,
    moveSource,
    setMoveSource,
    renameTarget,
    setRenameTarget,
    removeTarget,
    setRemoveTarget,
    peerLabelTarget,
    setPeerLabelTarget,
    closeAllDialogs,
  };
}
